import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef, Input } from "@angular/core";
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from "@angular/platform-browser";

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';

import { SharedService } from '../shared';

import { cloneDeep, isInteger, isUndefined } from "lodash";
import {
    IQuestionnaireResponse, IQuestionnaireSection, IQuestionnaireSubSection, IQuestionnaireQuestion,
    EnumSectionStatus, EnumQuestionType, getMinMonth, getMaxMonth, isValidDate, QuestionnaireStatusEnum, freeTextInputs,
    EnumFreeTextType, EmptyAnswer, EnumSectionType
} from "./questionnaire.model";
import {
    QuestionnaireJSON, CannabisarzneimittelChoice, CannabisextraktSubSection, CannabisblutenSubSection,
    CannabisarzneimittelSubSection, NebeneffektenSubSection,
    YES, NO, YES_PROBLEMS, NO_PROBLEMS, NO_LICENSE, QuestCannabisarzneimittelTitle, CANNABIS_ADDITIONAL_MEDICATION_QUESTION,
    FREIZEIT_TEXT, NONE_OPTION, CANNABIS_INTAKE_SCHEDULE_QUESTION, DAILY_SCHEMA, ON_DEMAND, BOTH, BTN_NEXT, BTN_END
} from './questionnaire';

import { SvgRatingComponent } from './svg-rating/svg-rating.component';
import { ScaleSliderComponent } from './scale-slider/scale-slider.component';
import { RuntimeConfigService } from "../shared/runtime-config.service";


@Component({
    selector: 'app-questionnaire',
    standalone: true,
    imports: [
        CommonModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        NgbModule,
        NgSelectModule,
        BsDatepickerModule,
        SvgRatingComponent,
        ScaleSliderComponent
    ],
    templateUrl: "./questionnaire.component.html",
    styleUrls: ["./questionnaire.component.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: [SharedService, RuntimeConfigService]
})


export class QuestionnaireComponent implements OnInit {
    showLoader: boolean = false;
    showConfirmation: boolean = false;

    UniqueId: string = null;
    questId: number = 0;

    questResponseData: any[] = [];

    QuestionnaireJSON: IQuestionnaireSection[] = [];
    EnumSectionStatus = EnumSectionStatus;
    EnumQuestionType = EnumQuestionType;
    EnumFreeTextType = EnumFreeTextType;

    minMonth: Date = null;
    maxMonth: Date = null;

    lastUpdated: any;

    basePath: string = this.config.BasePath;

    maxCannabisCount = 5;

    get questProgress() {
        let progress = this.getProgress();

        if (this.showConfirmation) {
            progress = 100;
        }

        return progress;
    }

    constructor(private cd: ChangeDetectorRef,
        private sharedService: SharedService,
        private sanitizer: DomSanitizer,
        private config: RuntimeConfigService) {
    }

    ngOnInit(): void {
        this.fnGetUniqueId();
    }

    bypassSecurityTrustHtml(html) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    fnGetUniqueId() {
        this.sharedService.getQuestUniqueId().subscribe({
            next: (res) => {
                this.questId = 0;
                this.UniqueId = res.UniqueId;
                this.fnLoadQuestionnaire();
            },
            error: (err) => {
                console.error(err);
            }
        })
    }

    fnLoadQuestionnaire() {
        this.minMonth = getMinMonth(1980, 0);
        this.maxMonth = getMaxMonth(1, true);

        this.QuestionnaireJSON = cloneDeep(QuestionnaireJSON);
        if (this.QuestionnaireJSON.length) {
            this.QuestionnaireJSON[0].status = EnumSectionStatus.ACTIVE;
            this.QuestionnaireJSON[0].subSections[0].status = EnumSectionStatus.ACTIVE;
        }
        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.Loaded);
    }

    getProgress() {
        let current: number = 0;
        let pagesLeft: number | null = null;

        for (const section of this.QuestionnaireJSON) {

            if (
                section.hidden ||
                ![EnumSectionStatus.ACTIVE, EnumSectionStatus.COMPLETED].includes(section.status)
            ) {
                continue;
            }

            for (const subSection of section.subSections) {

                if (
                    subSection.hidden ||
                    ![EnumSectionStatus.ACTIVE, EnumSectionStatus.COMPLETED].includes(subSection.status)
                ) {
                    continue;
                }

                current++;

                if (section.number === 7) {
                    if (subSection.number === 1) {
                        pagesLeft = 3;
                    } else if (subSection.number === 2) {
                        pagesLeft = 2;
                    }
                }
            }
        }

        return this.progressPercent(current, pagesLeft);
    }

    progressPercent(i: number, pagesLeft: number | null /* null or 3/2/1 */) {
        const FIRST = 7;
        const MAX = 42;
        const AVG = 20;
        const FINAL = 3;
        const SPAN = 100 - FIRST; // 93

        let p: number;

        const safeDiv = (d: number) => d <= 0 ? 0 : SPAN / d;

        if (pagesLeft !== null && pagesLeft !== undefined) {
            // Mode B: final pages
            const total = i + pagesLeft - 1;
            p = FIRST + (i - 1) * safeDiv(total - 1);
        } else {
            // Mode A: end not known
            if (i <= (AVG - FINAL)) { // i <= 17
                p = FIRST + (i - 1) * safeDiv(AVG - 1);
            } else {
                const estimatedTotal = Math.min(MAX, Math.max(AVG, i + FINAL));
                p = FIRST + (i - 1) * safeDiv(estimatedTotal - 1);
            }
        }

        p = Math.min(100, Math.max(FIRST, p));

        //return Number.isInteger(p) ? p : Number(p.toFixed(1));
        return Math.round(p);
    }


    isFreeTextShow(question: IQuestionnaireQuestion) {
        if (question.freeTextInput) {
            if (question.questionType == EnumQuestionType.DROPDOWN || question.questionType == EnumQuestionType.CHOICE) {
                let value = question.answer.value;
                if (value) {
                    if (question.multiple) {
                        var matched = freeTextInputs.filter(e => value.indexOf(e) !== -1);
                        return matched.length > 0;
                    } else {
                        return freeTextInputs.indexOf(value) > -1;
                    }
                }
            }
        }
        return false;
    }

    setConditonalOptions(JSONData: IQuestionnaireSection[], question: IQuestionnaireQuestion, onChagne: boolean) {
        if (question.conditionalOptions) {
            let dependedKey = question.conditionalOptions.dependedKey;
            if (!question.conditionalOptions.options) {
                let value = question.answer?.value?.[0];
                JSONData.forEach(_section => {
                    _section.subSections.forEach(_subSection => {
                        _subSection.questions.forEach(_question => {
                            if (_question.conditionalOptions) {
                                let _dependedKey = _question.conditionalOptions.dependedKey;
                                let _options = _question.conditionalOptions.options;
                                if (_options && dependedKey === _dependedKey) {
                                    let _option = _options.find(a => a.label == value);
                                    let data = _option ? _option.options || [] : [];
                                    if (onChagne) {
                                        _question.answer = EmptyAnswer();
                                    }
                                    _question.data = data;
                                }
                            }
                        })
                    })
                })
            }
        }
    }

    fnHandleCannibsFlow(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        this.cd.detectChanges();

        subSection.questions.forEach(question => {
            if (question.title?.includes(CANNABIS_ADDITIONAL_MEDICATION_QUESTION)) {
                const answer = question.answer.value?.[0];
                if (answer === YES) {
                    this.fnActivateNextCannabisBlock(section, subSection);
                } else {
                    this.fnRemoveNextCannabisBlocks(section, subSection);
                }
            }
            if (question.title?.includes(QuestCannabisarzneimittelTitle)) {
                this.fnApplyCannabisDetails(section, subSection, question);
            }
        })
        this.cd.detectChanges();
    }

    fnRemoveNextCannabisBlocks(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        section.subSections = section.subSections.filter(ss => ss.number <= subSection.number);
    }

    fnActivateNextCannabisBlock(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        let subSections = section.subSections.filter(ss => ss.number > subSection.number);
        if (subSections.length) return;

        let activeCount = 0;

        section.subSections.forEach(ss => {
            if (
                ss.questions.some(q =>
                    q.title?.includes(QuestCannabisarzneimittelTitle)
                ) &&
                !ss.hidden
            ) {
                activeCount++;
            }
        });

        if (activeCount >= this.maxCannabisCount) return;

        const nextCannibListNum = activeCount == 1 ? 5 : activeCount == 2 ? 9 : activeCount == 3 ? 13 : 17;
        section.subSections.push(CannabisarzneimittelChoice(nextCannibListNum));
    }

    fnApplyCannabisDetails(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion) {
        const answer = question.answer?.value?.[0];

        const baseNumber =
            subSection.number == 1 ? 2 :
                subSection.number == 5 ? 6 :
                    subSection.number == 9 ? 10 :
                        subSection.number == 13 ? 14 :
                            18;

        let detailSections: IQuestionnaireSubSection[] = [];
        switch (answer) {
            case 'Cannabisextrakt': {
                detailSections = CannabisextraktSubSection(baseNumber);
                break;
            }
            case 'Cannabisblüten': {
                detailSections = CannabisblutenSubSection(baseNumber);
                break;
            }
            default: {
                detailSections = CannabisarzneimittelSubSection(baseNumber);
                break;
            }
        }

        let subSections = section.subSections.filter(ss => ss.number > subSection.number);

        if (subSections.length && subSections.some(subSec => subSec.number == baseNumber && subSec.questions.some(q => q.prefix == answer))) return;

        section.subSections = section.subSections.filter(ss => ss.number <= subSection.number);
        section.subSections.push(...detailSections);
    }

    fnShowHideQuestions(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion) {
        if (question.title?.includes(CANNABIS_INTAKE_SCHEDULE_QUESTION)) {
            const answer = question.answer?.value?.[0];

            const nextSubSection = section.subSections?.find(s => s.number === subSection.number + 1);

            if (!nextSubSection) return;

            const total = nextSubSection.questions.length;

            nextSubSection.questions.forEach((q, i) => {
                q.hidden =
                    answer === BOTH
                        ? false
                        : answer === DAILY_SCHEMA
                            ? i >= total - 2        // hide last 2
                            : answer === ON_DEMAND
                                ? i < total - 2       // hide first (total - 2)
                                : true;
            });
        }

        if (section.number == 6) {
            if (subSection.number === 1 && question.number == 1) {
                const answer = question.answer?.value?.[0];

                section.subSections
                    ?.filter(s => s.number !== 1)
                    .forEach(subSec => {
                        if (answer === NO_LICENSE) {
                            // Hide all except SubSection 4
                            subSec.hidden = subSec.number !== 4;

                            // In SubSection 4 hide Q1–Q5
                            if (subSec.number == 4) {
                                subSec.questions
                                    ?.filter(q => q.number <= 5)
                                    .forEach(q => q.hidden = true);
                            }
                            return;
                        }

                        if (subSec.number === 2) {
                            subSec.hidden = answer !== YES_PROBLEMS;
                            return;
                        }
                        if (subSec.number === 3) {
                            subSec.hidden =
                                ![YES_PROBLEMS, NO_PROBLEMS].includes(answer);
                            return;
                        }
                    });
            }

            if (subSection.number === 3 && question.number == 2) {
                const answer = question.answer?.value?.[0];
                const targetSubSection = section.subSections?.find(s => s.number === 4);
                targetSubSection?.questions
                    ?.filter(q => q.number <= 5)
                    .forEach(q => q.hidden = answer !== YES);
            }

            if (subSection.number == 4 && question.number == 5) {
                const answer = question.answer?.value?.[0];
                subSection.questions.find(q => q.number === 6)!.hidden = answer !== YES;

            }
        }

        if (section.number == 7) {
            if (subSection.number === 1 && question.number == 1) {
                const answer = question.answer?.value?.[0];
                const targetSubSection = section.subSections?.find(s => s.number === 2);
                targetSubSection!.hidden = answer !== FREIZEIT_TEXT;

                subSection.button.text =
                    answer === FREIZEIT_TEXT ? BTN_NEXT : BTN_END;
            }
            if (subSection.number == 2 && question.number == 3) {
                const answer = question.answer?.value?.[0];
                subSection.questions.find(q => q.number === 4)!.hidden = answer !== YES;
            }
        }
    }

    fnUpdateBeforeGoToNext(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        this.fnHandleCannibsFlow(section, subSection);

        if (section.number == 4 && subSection.number == 1) {
            const firstAnswer = subSection.questions?.[0]?.answer?.value?.[0];
            const targetSection = this.QuestionnaireJSON.find(sec => sec.number === 5);
            if (!targetSection) return;

            if (firstAnswer === NONE_OPTION) {
                targetSection.hidden = true;
                targetSection.subSections = [];
            } else {
                targetSection.hidden = false;

                const selectedValues = subSection.questions.flatMap(q => q.answer?.value || []);
                const existingSubSections = targetSection.subSections || [];

                const subSections: IQuestionnaireSubSection[] = [];
                for (let i = 0; i < selectedValues.length; i++) {
                    let num = i + 1;
                    let label = selectedValues[i];
                    let exist = existingSubSections.find(a => a.answerValue == label);

                    if (exist) {
                        subSections.push({
                            ...exist,
                            number: num
                        });
                    } else {
                        subSections.push(NebeneffektenSubSection(label, num));
                    }
                }
                targetSection.subSections = subSections;
            }
        }
        this.cd.detectChanges();
    }


    changeFunction(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion) {
        if (subSection.status == EnumSectionStatus.COMPLETED) {
            return;
        }
        this.setConditonalOptions(this.QuestionnaireJSON, question, true);
        if (!this.isFreeTextShow(question)) {
            question.answer.freeText = null;
        }
        question.hasError = false;
        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress, 100);

    }

    freeTextChangeFunction(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion) {
        question.hasError = false;
        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress, 100);
    }

    fnRatingChange(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion, value: any) {
        if (subSection.status == EnumSectionStatus.COMPLETED) {
            return;
        }
        question.answer.value = value;
        question.hasError = false;
        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress, 100);
    }

    fnScaleChange(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion, value: any) {
        if (subSection.status == EnumSectionStatus.COMPLETED) {
            return;
        }
        question.answer.value = value;
        question.hasError = false;
        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress, 100);
    }

    isChoiceSelected(question: IQuestionnaireQuestion, choice: string) {
        let value = question.answer.value || [];
        if (Array.isArray(value)) {
            return value.indexOf(choice) > -1;
        }
        return false;
    }

    fnDisabledChoice(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion, choice: string) {
        if (subSection.status == EnumSectionStatus.COMPLETED) {
            return true;
        }

        // 🔥 Nebeneffekte exclusive logic
        if (section.number == 4 && subSection.sectionType === EnumSectionType.Nebeneffekte) {
            let selectedValues: string[] = [];
            subSection.questions.forEach(quest => {
                let values = quest.answer.value || [];
                selectedValues = [...selectedValues, ...values];
            })

            // If "Keine Effekte..." is already selected → disable all others
            if (
                selectedValues.includes(NONE_OPTION) &&
                choice !== NONE_OPTION
            ) {
                return true;
            }

            // If any other option is selected → disable "Keine Effekte..."
            if (
                choice === NONE_OPTION &&
                selectedValues.some(v => v !== NONE_OPTION)
            ) {
                return true;
            }
        }
        return false;
    }

    fnSelectChoice(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, question: IQuestionnaireQuestion, choice: string) {
        if (subSection.status == EnumSectionStatus.COMPLETED) {
            return;
        }

        let value = question.answer.value || [];
        if (question.multiple) {
            let index = value.indexOf(choice);
            if (index == -1) {
                value.push(choice);
            } else {
                value.splice(index, 1);
            }
        } else {
            value = [choice];
        }


        if (subSection.required) {
            subSection.questions.forEach(quest => {
                quest.hasError = false;
            })
        }

        question.hasError = false;
        question.answer.value = value;

        this.setConditonalOptions(this.QuestionnaireJSON, question, true);
        if (!this.isFreeTextShow(question)) {
            question.answer.freeText = null;
        }
        this.fnShowHideQuestions(section, subSection, question);
        this.cd.detectChanges();

        // Save Progress
        this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress, 100);
    }

    fnSetQuestionHasError(question: IQuestionnaireQuestion, showNext: boolean) {
        let value = question.answer.value;
        switch (question.questionType) {
            case EnumQuestionType.INPUT:
            case EnumQuestionType.TEXTAREA:
                if (!!!value) {
                    question.hasError = true;
                } else {
                    question.hasError = false;
                }
                if (question.pattern) {
                    if (!question.pattern.test(value)) {
                        question.hasError = true;
                    }
                }
                break;
            case EnumQuestionType.MONTH:
                if (!!!value) {
                    question.hasError = true;
                } else {
                    if (isValidDate(value)) {
                        question.hasError = false;
                    } else {
                        question.hasError = true;
                    }
                }
                if (question.pattern) {
                    if (!question.pattern.test(value)) {
                        question.hasError = true;
                    }
                }
                break;
            case EnumQuestionType.DROPDOWN:
                if (question.multiple) {
                    if (!!!value || !value.length) {
                        question.hasError = true;
                    } else {
                        question.hasError = false;
                    }
                } else {
                    if (!!!value) {
                        question.hasError = true;
                    } else {
                        question.hasError = false;
                    }
                }
                break;
            case EnumQuestionType.CHOICE:
                if (!!!value || !value.length) {
                    question.hasError = true;
                } else {
                    question.hasError = false;
                }
                break;
            case EnumQuestionType.RATING:
                if (!!!value) {
                    question.hasError = true;
                } else {
                    question.hasError = false;
                }
                break;
            case EnumQuestionType.SCALE:
                if (!!!value) {
                    question.hasError = true;
                } else {
                    question.hasError = false;
                }
                break;
        }

        if (this.isFreeTextShow(question)) {
            value = question.answer.freeText;
            if (!!!value) {
                question.hasError = true;
            } else {
                question.hasError = false;
            }
        }


        if (question.hasError) {
            showNext = false;
        }
        return showNext;
    }

    fnSetCamSectionHasError(section: IQuestionnaireSection, subSection: IQuestionnaireSubSection, showNext) {
        subSection.questions.forEach(question => {
            if (!question.hidden && question.required) {
                showNext = this.fnSetQuestionHasError(question, showNext);
            }
        })

        const requiredGroupQuestions = subSection.questions
            .filter(q => !isUndefined(q.requiredGroup) && !q.hidden);
        if (requiredGroupQuestions.length) {
            const valueExist = requiredGroupQuestions.some(
                q => this.fnSetQuestionHasError(q, true)
            );
            if (valueExist) {
                requiredGroupQuestions.forEach(quest => {
                    quest.hasError = false;
                })
            }
            showNext = showNext && valueExist;
        }

        return showNext;
    }



    fnSubmitForm(sIndex: number, ssIndex: number, section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        this.cd.detectChanges();
        this.fnValidateForm(sIndex, ssIndex, section, subSection);
    }

    fnValidateForm(sIndex: number, ssIndex: number, section: IQuestionnaireSection, subSection: IQuestionnaireSubSection) {
        let showNext: boolean = true;
        switch (subSection.sectionType) {

            default:
                if (section.number == 3) {
                    showNext = this.fnSetCamSectionHasError(section, subSection, showNext);
                } else {
                    subSection.questions.forEach(question => {
                        if (!question.hidden && question.required) {
                            showNext = this.fnSetQuestionHasError(question, showNext);
                        }
                    })
                }
                break;
        }


        if (showNext && subSection.required) {
            let showError = true;
            subSection.questions.forEach(question => {
                let value = question.answer.value;
                switch (question.questionType) {
                    case EnumQuestionType.INPUT:
                    case EnumQuestionType.MONTH:
                        if (!!value) {
                            showError = false;
                        }
                        break;
                    case EnumQuestionType.TEXTAREA:
                        value = this.fnExtractContent(value);
                        if (!!value) {
                            showError = false;
                        }
                        break;
                    case EnumQuestionType.DROPDOWN:
                        if (question.multiple) {
                            if (value && value.length) {
                                showError = false;
                            }
                        } else {
                            if (value) {
                                showError = false;
                            }
                        }
                        break;
                    case EnumQuestionType.CHOICE:
                        if (value && value.length) {
                            showError = false;
                        }
                        break;
                    case EnumQuestionType.RATING:
                        if (!!value) {
                            showError = false;
                        }
                        break;
                    case EnumQuestionType.SCALE:
                        if (!!value) {
                            showError = false;
                        }
                        break;
                }

                if (this.isFreeTextShow(question)) {
                    value = question.answer.freeText;
                    if (!!value) {
                        showError = false;
                    }
                }
            })

            if (showError) {
                subSection.questions.forEach(question => {
                    if (!question.hidden) {
                        showNext = this.fnSetQuestionHasError(question, showNext);
                    }
                });
            }
        }

        if (showNext) {
            subSection.hasError = false;
            this.fnUpdateBeforeGoToNext(section, subSection);
            this.fnGoToNextSection(sIndex, ssIndex);
        } else {
            subSection.hasError = true;
            this.navigateToSection(sIndex, ssIndex);
        }
        this.cd.detectChanges();
        return showNext;
    }

    fnGoToPrevSection(sIndex: number, ssIndex: number) {
        let prevSIndex = sIndex;
        let prevSSIndex = ssIndex;
        let sLength = this.QuestionnaireJSON.length;

        for (var i = 0; i < sLength; i++) {
            let subSections = this.QuestionnaireJSON[i].subSections;
            for (var j = 0; j < subSections.length; j++) {
                if (i > sIndex || (i >= sIndex && j >= ssIndex)) {
                    this.QuestionnaireJSON[i].status = null;
                    this.QuestionnaireJSON[i].subSections[j].status = null;
                } else {
                    prevSIndex = i;
                    prevSSIndex = j;
                }
            }
        }
        if (prevSIndex >= 0) {

            this.QuestionnaireJSON[prevSIndex].status = EnumSectionStatus.ACTIVE;
            if (this.QuestionnaireJSON[prevSIndex].subSections.length) {
                this.QuestionnaireJSON[prevSIndex].subSections[prevSSIndex].status = EnumSectionStatus.ACTIVE;
            }

            // Save Progress
            this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress);
            if (this.QuestionnaireJSON[prevSIndex].hidden || this.QuestionnaireJSON[prevSIndex].subSections[prevSSIndex].hidden) {
                this.fnGoToPrevSection(prevSIndex, prevSSIndex);
            }
            else {
                if (prevSIndex != sIndex || prevSSIndex != ssIndex) {
                    this.navigateToSection(prevSIndex, prevSSIndex);
                }
            }
        }

    }

    fnGoToNextSection(sIndex: number, ssIndex: number) {
        let nextSIndex = sIndex;
        let nextSSIndex = ssIndex;
        let sLength = this.QuestionnaireJSON.length;
        var IsQuestEnd = false;

        if (sIndex == 0 && ssIndex == 0) {
            let secQuestAns1 = this.QuestionnaireJSON[sIndex]?.subSections[ssIndex]?.questions[0]?.answer?.value || [""];
            let secQuestAns2 = this.QuestionnaireJSON[sIndex]?.subSections[ssIndex]?.questions[1]?.answer?.value || [""];
            if (secQuestAns1[0] == NO || secQuestAns2[0] == NO) {
                IsQuestEnd = true;
            }
        }

        if (!IsQuestEnd) {
            for (var i = 0; i < sLength; i++) {
                let subSections = this.QuestionnaireJSON[i].subSections;
                var ssLength = subSections.length;
                if (ssLength == 0 || ssIndex == ssLength - 1) {
                    if (sIndex == i) {
                        this.QuestionnaireJSON[sIndex].status = EnumSectionStatus.COMPLETED;
                        if (this.QuestionnaireJSON[sIndex].subSections.length) {
                            this.QuestionnaireJSON[sIndex].subSections[ssIndex].status = EnumSectionStatus.COMPLETED;
                        }
                        nextSIndex = i + 1;
                        nextSSIndex = 0;
                    }
                }
                else {
                    for (var j = 0; j < subSections.length; j++) {
                        if (sIndex == i && ssIndex == j) {
                            if (this.QuestionnaireJSON[sIndex].subSections.length) {
                                this.QuestionnaireJSON[sIndex].subSections[ssIndex].status = EnumSectionStatus.COMPLETED;
                            }
                            nextSSIndex = j + 1;
                        }
                    }
                }
            }
        }

        if (!IsQuestEnd && nextSIndex < sLength) {

            this.QuestionnaireJSON[nextSIndex].status = EnumSectionStatus.ACTIVE;
            if (this.QuestionnaireJSON[nextSIndex].subSections.length) {
                this.QuestionnaireJSON[nextSIndex].subSections[nextSSIndex].status = EnumSectionStatus.ACTIVE;
            }

            // Save Progress
            this.fnGenerateResponse(QuestionnaireStatusEnum.InProgress);

            if (this.QuestionnaireJSON[nextSIndex].hidden || this.QuestionnaireJSON[nextSIndex].subSections[nextSSIndex].hidden) {
                this.fnGoToNextSection(nextSIndex, nextSSIndex);
            } else {
                if (nextSIndex != sIndex || nextSSIndex != ssIndex) {
                    this.navigateToSection(nextSIndex, nextSSIndex);
                }
            }
        } else {
            this.fnGenerateResponse(QuestionnaireStatusEnum.Completed);
        }
    }

    navigateToSection(sIndex, ssIndex) {
        this.lastUpdated = new Date().getTime();
        this.cd.detectChanges();

        window.scrollTo({
            top: 160,
            behavior: "smooth"
        });

        return;
        setTimeout(() => {
            const elmId = `questionnnaire_section_${sIndex}_subsection_${ssIndex}`;
            const elm = document.getElementById(elmId) as HTMLElement;

            if (elm) {
                const marginTop = 20;

                const y =
                    elm.getBoundingClientRect().top +
                    window.pageYOffset -
                    marginTop;

                window.scrollTo({
                    top: y,
                    behavior: "smooth"
                });
            }
        })
    }

    navigateToConfirmScreen() {
        this.cd.detectChanges();
        this.showConfirmation = true;

        window.scrollTo({
            top: 160,
            behavior: "smooth"
        });

        return;
        setTimeout(() => {
            const elmId = 'questionnaire_section_confirm_screen';
            const elm = document.getElementById(elmId);

            if (elm) {
                const marginTop = 20;

                // Element position relative to the page
                const y =
                    elm.getBoundingClientRect().top +
                    window.pageYOffset -
                    marginTop;

                window.scrollTo({
                    top: y,
                    behavior: 'smooth'
                });
            }
        })
    }


    fnGenerateResponse(status: QuestionnaireStatusEnum, delay = 0) {
        let responseJSON: IQuestionnaireResponse[] = [];

        this.QuestionnaireJSON.forEach((section, sIndex) => {
            if (section.hidden || ![EnumSectionStatus.ACTIVE, EnumSectionStatus.COMPLETED].includes(section.status)) {
                return;
            }
            section.subSections.forEach((subSection, ssIndex) => {
                if (subSection.hidden || ![EnumSectionStatus.ACTIVE, EnumSectionStatus.COMPLETED].includes(subSection.status)) {
                    return;
                }
                var responseObj: IQuestionnaireResponse = {
                    sectionNumber: section.number,
                    sectionText: this.fnExtractContent(section.title || ''),
                    subSectionNumber: subSection.number,
                    subSectionText: this.fnExtractContent(subSection.title || ''),
                    subSectionType: subSection.sectionType || ''
                }
                switch (subSection.sectionType) {
                    default:
                        subSection.questions.forEach((question, qIndex) => {
                            if (!question.hidden) {
                                responseJSON.push({
                                    ...responseObj,
                                    questionNumber: question.number,
                                    questionPrefix: question.prefix || '',
                                    questionText: this.fnExtractContent(question.title || ''),
                                    questionType: question.questionType,
                                    response: question.answer
                                })
                            }
                        })
                        break;
                }
            })
        })

        this.questResponseData = responseJSON;

        let payload: any = {
            UniqueId: this.UniqueId,
            Status: status,
            JsonData: this.questResponseData
        }
        if (status == QuestionnaireStatusEnum.Completed) {
            this.fnSubmitQuestData(payload, delay);
        } else {
            this.fnSaveQuestData(payload, delay);
        }
    }

    timerSaveQuestData: any;
    fnSaveQuestData(payload, delay) {
        if (this.showConfirmation) {
            return;
        }
        clearTimeout(this.timerSaveQuestData);
        this.timerSaveQuestData = setTimeout(() => {
            this.sharedService.saveQuestData(payload).subscribe({
                next: (res) => {
                    if (res.New) {
                        this.GenerateNewUniqueId();
                    }
                },
                error: (err) => {
                    console.error(err);
                }
            });
        }, delay);
    }

    fnSubmitQuestData(payload, delay) {
        if (this.showLoader || this.showConfirmation) {
            return;
        }
        clearTimeout(this.timerSaveQuestData);
        this.showLoader = true;
        this.timerSaveQuestData = setTimeout(() => {
            if (this.questId) {
                payload["Id"] = this.questId;
                this.sharedService.updateQuestData(this.questId, payload).subscribe({
                    next: (res) => {
                        if (res.New) {
                            this.GenerateNewUniqueId();
                        } else {
                            this.navigateToConfirmScreen();
                        }
                        this.showLoader = false;
                    },
                    error: (err) => {
                        console.error(err);
                        this.showLoader = false;
                    }
                });
            } else {
                this.sharedService.saveQuestData(payload).subscribe({
                    next: (res) => {
                        if (res.New) {
                            this.GenerateNewUniqueId();
                        } else {
                            this.questId = res.Id;
                            this.navigateToConfirmScreen();
                        }
                        this.showLoader = false;
                    },
                    error: (err) => {
                        console.error(err);
                        this.showLoader = false;
                    }
                });
            }
        }, delay);
    }



    fnExtractContent(html, space = true) {
        var divTag = document.createElement('div');
        divTag.innerHTML = html;
        if (space) {
            var children = divTag.querySelectorAll('*');
            for (var i = 0; i < children.length; i++) {
                children[i].textContent += ' ';
            }
        }
        return [divTag.textContent || divTag.innerText].toString().replace(/ +/g, ' ').trim();
    }

    GenerateNewUniqueId() {
        this.fnGetUniqueId();
    }
}
